/* eslint-disable @typescript-eslint/indent */
/*
   Note: This Thermometer component has a lot of the styling inverted due to its vertical
   orientation and use of rotation transforms.
   Be sure to carefully review any changes to the styling of this component to avoid unexpected
   effects and ensure visual consistency.
*/

// Core
import styled from 'styled-components';

// Libraries
import { Progress as ProgressAntD } from 'antd';
import _ from 'lodash';

// Config
import { BREAKPOINTS } from '../../../config/screen';

// Types
import { Theme, ThemedProps } from '../../../types/theme';
import { Thresholds } from '../../../types/global';

// Component
import { ThermometerProps } from './Thermometer.types';

/*
  Generates CSS styles to display threshold ticks on the thermometer
  @param threshold receives a record of threshold values
  @param theme receives the theme object
  @param running indicates if the thermometer is running
  @param hasValidationError indicates if there's a threshold validation error
  @returns a string of CSS styles
*/
const showThresholdTicks = (
  threshold: Thresholds,
  theme: Theme,
  running: boolean,
  hasValidationError: boolean = false,
): string => {
  const mapStepColor = (
    thresholdValue: number | null | undefined,
    backgroundColor: string,
  ): string => {
    if (_.isNil(thresholdValue) || thresholdValue <= 0) {
      return '';
    }

    return `
    .ant-progress-steps-item:nth-child(${thresholdValue}) {
      background-color: ${
        running && !hasValidationError
          ? backgroundColor
          : theme.color.thermometer.offThresholdTickBackgroundColor
      };
    };
  `;
  };

  return `
    ${mapStepColor(
      threshold.highWarning,
      theme.color.thermometer.tickHighWarningBackgroundColor,
    )}
    ${mapStepColor(
      threshold.highAlarm,
      theme.color.thermometer.tickHighAlarmBackgroundColor,
    )}
    ${mapStepColor(
      threshold.lowWarning,
      theme.color.thermometer.tickLowWarningBackgroundColor,
    )}
    ${mapStepColor(
      threshold.lowAlarm,
      theme.color.thermometer.tickLowAlarmBackgroundColor,
    )}
  `;
};

/*
  Styled component for the Thermometer element
  @param value receives the current thermometer value
  @param thresholds receives the threshold object
  @param running indicates if the thermometer is running
  @param theme receives the theme object
*/
export const Element = styled.div<Pick<
  ThermometerProps, 'value' | 'thresholds' | 'running'> & ThemedProps
>`
  height: 210px;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  /* ******* VALUES STYLES ******* */
  .max-value-container,
  .min-value-container {
    font-family: ${({ theme }) => theme.font.regular};
    color: ${({ theme }) => theme.color.thermometer.minAndMaxValueTextColor};
    transform: rotate(90deg);
    margin-top: 16px;
    font-size: 12px;
    width: 10px;
    height: 10px;
  };

  /* ******* VALUE CONTAINER ******* */
  .value-container {
    transform: rotate(90deg);
    font-family: ${({ theme }) => theme.font.regular};
    margin-top: 4px;
    font-size: 18px;
    white-space: nowrap;
  };

  /* ******* SMALL TABLET (sm to md) ******* */
  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    height: 189px;

    .max-value-container,
    .min-value-container {
      margin-top: 14px;
      font-size: 11px;
      width: 9px;
      height: 9px;
    };

    .value-container {
      margin-top: 7px;
      font-size: 16px;
    };
  };

  /* ******* SMALL MOBILE (xs to sm) ******* */
  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    height: 178px;

    .max-value-container,
    .min-value-container {
      margin-top: 13px;
      font-size: 10.5px;
      width: 8.5px;
      height: 8.5px;
    };

    .value-container {
      margin-top: 6.5px;
      font-size: 15px;
    };
  };

  /* ******* SMALL MOBILE (iPhone SE) ******* */
  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    height: 168px;

    .max-value-container,
    .min-value-container {
      margin-top: 12px;
      font-size: 10px;
      width: 8px;
      height: 8px;
    };

    .value-container {
      margin-top: 6px;
      font-size: 14px;
    };
  };
`;

/* Styled component for the container of the Thermometer */
export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-direction: column;
  height: 145px;
  position: relative;

  /* ******* SMALL TABLET (sm to md) ******* */
  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    height: 130px;
  };

  /* ******* SMALL MOBILE (xs to sm) ******* */
  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    height: 122px;
  };

  /* ******* SMALL MOBILE (iPhone SE) ******* */
  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    height: 115px;
  };
`;

/* Styled component for the container of progress indicators */
export const ProgressIndicatorsContainer = styled.div`
  transform: rotate(270deg);
`;

/* Styled component for the AntD progress bar */
export const ThermometerAntD = styled(ProgressAntD)`
  position: relative;
  top: 0;
  bottom: 0;
  right: 0;

  /* ******* PROGRESS ******* */
  .ant-progress-small {
    width: 10px;
  };

  /* ******* PROGRESS STEPS ******* */
  .ant-progress-steps-item {
    width: 1px;
  };

  /* ******* SMALL TABLET (sm to md) ******* */
  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    .ant-progress-small {
      width: 9px !important;
    };

    .ant-progress {
      width: 153px !important;
    };

    .ant-progress-outer {
      width: 153px !important;
    };

    .ant-progress-inner {
      width: 153px !important;
    };
  };

  /* ******* SMALL MOBILE (xs to sm) ******* */
  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    .ant-progress-small {
      width: 8px !important;
    };

    .ant-progress {
      width: 135px !important;
    };

    .ant-progress-outer {
      width: 135px !important;
    };

    .ant-progress-inner {
      width: 135px !important;
    };

    .ant-progress-steps-item {
      width: 0.9px !important;
    };
  };

  /* ******* SMALL MOBILE (iPhone SE) ******* */
  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    .ant-progress-small {
      width: 7px !important;
    };

    .ant-progress {
      width: 120px !important;
    };

    .ant-progress-outer {
      width: 120px !important;
    };

    .ant-progress-inner {
      width: 120px !important;
    };

    .ant-progress-steps-item {
      width: 0.8px !important;
    };
  };
`;

/*
  Styled component for the steps of the thermometer
  @param threshold receives a record of threshold values
  @param running indicates if the thermometer is running
  @param hasValidationError indicates if there's a threshold validation error
*/
export const StepsThermometer = styled.div<{
  threshold: Thresholds,
  running: boolean,
  hasValidationError?: boolean,
}>`
  display: flex;
  position: absolute;
  top: 20px;
  left: 0;
  bottom: 0;
  right: 0;

  /* ******* PROGRESS STEPS ITEM ******* */
  .ant-progress-steps-item {
    position: relative;

    span {
      position: absolute;
      top: 12px;
      right: 0;
      font-size: 10px;
      transform: rotate(90deg);
    };
  };

  /* ******* THRESHOLD STEPS - Same size for all breakpoints ******* */
  .ant-progress-steps-item.high-alarm,
  .ant-progress-steps-item.high-warning,
  .ant-progress-steps-item.low-alarm,
  .ant-progress-steps-item.low-warning {
    height: 12px !important;
    width: 2px !important;
  };

  ${({
    threshold,
    theme,
    running,
    hasValidationError,
  }) => showThresholdTicks(threshold, theme, running, hasValidationError || false)};

  /* ******* PROGRESS STEPS OUTER ******* */
  .ant-progress-steps-outer {
    align-items: start;
  };

  /* ******* SMALL TABLET (sm to md) ******* */
  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    left: calc(50% - 76.5px);
    right: auto;
    width: 153px;

    .ant-progress {
      width: 153px !important;
    };

    .ant-progress-steps-outer {
      width: 153px !important;
    };

    .ant-progress-steps-item {
      span {
        top: 11px;
        font-size: 9px;
      };
    };
  };

  /* ******* SMALL MOBILE (xs to sm) ******* */
  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    top: 20px;
    left: calc(50% - 67.5px);
    right: auto;
    width: 135px;

    .ant-progress {
      width: 135px !important;
    };

    .ant-progress-steps-outer {
      width: 135px !important;
    };

    .ant-progress-steps-item {
      width: 0.9px !important;

      span {
        top: 10.5px;
        font-size: 8.5px;
      };
    };
  };

  /* ******* SMALL MOBILE (iPhone SE) ******* */
  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    top: 20px;
    left: calc(50% - 60px);
    right: auto;
    width: 120px;

    .ant-progress {
      width: 120px !important;
    };

    .ant-progress-steps-outer {
      width: 120px !important;
    };

    .ant-progress-steps-item {
      width: 0.8px !important;

      span {
        top: 10px;
        font-size: 8px;
      };
    };
  };
`;

/* Styled component for the progress thermometer */
export const ProgressThermometer = styled.div`
  width: 170px;
  height: 60px;

  .ant-progress {
    width: 170px !important;
  };

  .ant-progress-outer {
    width: 170px !important;
  };

  .ant-progress-inner {
    width: 170px !important;
  };

  /* ******* SMALL TABLET (sm to md) ******* */
  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    width: 153px;
    height: 54px;

    .ant-progress {
      width: 153px !important;
    };

    .ant-progress-outer {
      width: 153px !important;
    };

    .ant-progress-inner {
      width: 153px !important;
    };
  };

  /* ******* SMALL MOBILE (xs to sm) ******* */
  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    width: 135px;
    height: 47px;

    .ant-progress {
      width: 135px !important;
    };

    .ant-progress-outer {
      width: 135px !important;
    };

    .ant-progress-inner {
      width: 135px !important;
    };
  };

  /* ******* SMALL MOBILE (iPhone SE) ******* */
  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    width: 120px;
    height: 42px;

    .ant-progress {
      width: 122px !important;
    };

    .ant-progress-outer {
      width: 122px !important;
    };

    .ant-progress-inner {
      width: 122px !important;
    };
  };
`;

/* Styled component for the text container */
export const TextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  padding-top: 8px;

  /* ******* SMALL MOBILE (xs to sm) ******* */
  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    padding-top: 6px;
  };

  /* ******* SMALL MOBILE (iPhone SE) ******* */
  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    padding-top: 4px;
  };
`;
